/**
 * Balance / Billing / Wallet query+mutation hooks.
 *
 * Covers the reads/writes previously done imperatively (via useAppServices)
 * in:
 *   - pages/Settings/components/Balance/index.js (the "Billing" tab —
 *     payment info, invoices, subscription, AI-token purchases, the account
 *     balance + balance logs, and the 15s accounts.getbyid poll)
 *   - pages/Settings/components/Balance/component/{BuyTokensModal,
 *     EditPaymentMethodModal, EditBillingInfoModal, CancelConfirmModal}.jsx
 *   - pages/Settings/components/billing/index.js (the older/parallel
 *     "billing" tab — its own account/plan read + user.cancelsubscription)
 *   - pages/Settings/components/billing/component/CancelModal/CancelModal.js
 *     (second UI entry point to the same user.cancelsubscription action)
 *
 * `billingKeys.all(locationId)` is the shared prefix for every query fed by
 * Balance/index.js's old `refetch` toggle (payment info, invoices,
 * subscription, AI-token purchases, balance, balance logs). The old toggle
 * refetched all of them unconditionally on every write from every child
 * modal, so `invalidateBilling` reproduces that exactly with one
 * invalidateQueries call instead of picking apart which write affects which
 * query. accounts.getbyid is a separate root key (`accountKeys.byId`)
 * because it's also polled independently and is conceptually "account data"
 * rather than "billing page data".
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import localforage from "localforage";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT } = ReqMethods;

export const billingKeys = {
  all: (locationId) => ["billing", locationId],
  paymentInfo: (locationId, agencyId, customerId) => [
    ...billingKeys.all(locationId),
    "paymentInfo",
    agencyId,
    customerId,
  ],
  invoices: (locationId, agencyId, customerId) => [
    ...billingKeys.all(locationId),
    "invoices",
    agencyId,
    customerId,
  ],
  subscription: (locationId, subscriptionId, agencyId) => [
    ...billingKeys.all(locationId),
    "subscription",
    subscriptionId,
    agencyId,
  ],
  aiTokenPurchases: (locationId, accountId) => [
    ...billingKeys.all(locationId),
    "aiTokenPurchases",
    accountId,
  ],
  balance: (locationId) => [...billingKeys.all(locationId), "balance"],
  balanceLogs: (locationId, agencyId) => [
    ...billingKeys.all(locationId),
    "balanceLogs",
    agencyId,
  ],
};

export const accountKeys = {
  byId: (id) => ["account", id],
};

// Shared by every write below: reproduces the old `setRefetch((r) => !r)`
// toggle, which refetched all of Balance/index.js's data + the account poll
// regardless of which write triggered it.
export function invalidateBilling(queryClient, locationId) {
  queryClient.invalidateQueries({ queryKey: billingKeys.all(locationId) });
  queryClient.invalidateQueries({ queryKey: accountKeys.byId(locationId) });
}

export function usePaymentInfo(locationId, agencyId, customerId) {
  return useQuery({
    queryKey: billingKeys.paymentInfo(locationId, agencyId, customerId),
    queryFn: async () => {
      const res = await request(GET, `/subscriptions/agency/${agencyId}/${customerId}`);
      return res?.data ?? null;
    },
    enabled: !!agencyId && !!customerId,
  });
}

export function useInvoices(locationId, agencyId, customerId) {
  return useQuery({
    queryKey: billingKeys.invoices(locationId, agencyId, customerId),
    queryFn: async () => {
      const res = await request(GET, `/subscriptions/agency/invoices/${agencyId}/${customerId}`);
      return res?.data?.data || [];
    },
    enabled: !!agencyId && !!customerId,
  });
}

export function useSubscriptionById(locationId, subscriptionId, agencyId) {
  return useQuery({
    queryKey: billingKeys.subscription(locationId, subscriptionId, agencyId),
    queryFn: async () => {
      const res = await request(GET, `/subscriptions/get-subscription/${subscriptionId}/${agencyId}`);
      return res?.data ?? null;
    },
    enabled: !!subscriptionId && !!agencyId,
  });
}

export function useAITokenPurchases(locationId, accountId) {
  return useQuery({
    queryKey: billingKeys.aiTokenPurchases(locationId, accountId),
    queryFn: async () => {
      const res = await request(POST, "/accounts/ai-token-purchases", {
        payload: { account_id: accountId },
      });
      return Array.isArray(res?.data) ? res.data : [];
    },
    enabled: !!accountId,
  });
}

// BuyTokensModal.jsx's plan-picker list (GET /ai_token_plans) — a different
// endpoint than useAITokenPurchases above (that's purchase history; this is
// the agency's purchasable token packs). Not part of billingKeys/
// invalidateBilling since it's agency-level pricing config, not per-account
// billing data. `enabled` param lets callers gate the fetch on modal-open
// (the original effect only fetched while the modal was open).
export const aiTokenPlansKeys = {
  list: (agencyId) => ["aiTokenPlans", agencyId],
};

export function useAITokenPlans(agencyId, enabled = true) {
  return useQuery({
    queryKey: aiTokenPlansKeys.list(agencyId),
    queryFn: async () => {
      const res = await request(GET, "/ai_token_plans", { query: `agency_id=${agencyId}` });
      return res?.success && Array.isArray(res?.data) ? res.data : [];
    },
    enabled: enabled && !!agencyId,
  });
}

// account_id here is the same value as `locationId` at every call site
// (userActiveLocation.id) — kept as its own root key so Balance/index.js's
// 15s poll and any other consumer share one cache entry.
export function useAccountById(id, options = {}) {
  return useQuery({
    queryKey: accountKeys.byId(id),
    queryFn: async () => {
      const res = await request(GET, "/accounts/getbyid", { query: `id=${id}` });
      return res?.data ?? null;
    },
    enabled: !!id,
    ...options,
  });
}

export function useAccountBalance(locationId, id) {
  return useQuery({
    queryKey: billingKeys.balance(locationId),
    queryFn: async () => {
      const res = await request(POST, "/accounts/getBalance", { payload: { id } });
      return res?.data ?? null;
    },
    enabled: !!id,
  });
}

// Returns the raw `{ data, agency }` body (unwrapped) — same shape the old
// getBalanceLogs() callback read from.
export function useAccountBalanceLogs(locationId, agencyId) {
  return useQuery({
    queryKey: billingKeys.balanceLogs(locationId, agencyId),
    queryFn: () =>
      request(POST, "/accounts/getBalanceLogs", {
        payload: { account_id: locationId, agency_id: agencyId },
      }).then((res) => res ?? null),
    enabled: !!locationId,
  });
}

export function usePurchaseAITokens(locationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/purchase-ai-tokens", { payload }),
    onSuccess: (data) => {
      if (data?.PaymentDone && data?.success) invalidateBilling(queryClient, locationId);
    },
  });
}

export function useUpdatePaymentMethod(locationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/subscriptions/agency/paymentmethod", { payload }),
    onSuccess: (data) => {
      if (data?.data) invalidateBilling(queryClient, locationId);
    },
  });
}

export function useUpdateBillingInfo(locationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/subscriptions/agency/billinginfo", { payload }),
    onSuccess: (data) => {
      if (data?.data) invalidateBilling(queryClient, locationId);
    },
  });
}

// Balance/component/CancelConfirmModal.jsx's cancel action — a different
// backend route than the legacy billing/index.js cancel below.
export function useCancelSubscription(locationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/subscriptions/cancel-subscription", { payload }),
    onSuccess: (data) => {
      if (data?.success) invalidateBilling(queryClient, locationId);
    },
  });
}

// Legacy "billing" tab (pages/Settings/components/billing/index.js) — reads
// its ids from localforage (matching the original get_account_active_plane),
// not from context, so its query key has no dynamic parts.
const legacyBillingKey = ["billingLegacyActivePlan"];

export function useActiveAccountPlan() {
  return useQuery({
    queryKey: legacyBillingKey,
    queryFn: async () => {
      const account = await localforage.getItem("active_location");
      const agencyId = await localforage.getItem("agency");
      return request(GET, "/accounts/accounts", {
        query: `account_id=${account.id}&agency_id=${agencyId?._id}`,
      });
    },
  });
}

// Shared by billing/index.js's "Cancel Subscription" button and
// billing/component/CancelModal/CancelModal.js's "Send" form — same
// POST /user/cancelsubscription endpoint, two independent UI entry points.
// Neither call site invalidates billing queries here: preserves the
// original behavior exactly (billing/index.js's own button never refetched;
// CancelModal.js refetches by calling the parent's justCloseIt(), which
// still calls the legacy plan query's refetch()).
export function useCancelUserSubscription() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/user/cancelsubscription", { payload }),
  });
}

// Balance/index.js's mount-time fetch of chat-location match data. The
// original call read its id from `location_id._id` where `location_id` was
// the *whole* useUserInfo() tuple (not userActiveLocation) — so the id sent
// was always undefined. Preserved exactly as-is: this migration only swaps
// useHttp for useQuery, not that pre-existing argument bug.
export function useLocationDataByChat(locationId) {
  return useQuery({
    queryKey: ["locationDataByChat", locationId],
    queryFn: async () => {
      const res = await request(GET, `/chat/get_data_by_location/${locationId}`);
      return Array.isArray(res?.data) ? res.data : [];
    },
  });
}

// wallet/components/AddBalanceModal.jsx — two-step "charge, then confirm"
// flow (fired twice: once to create the pending charge, once with
// payment_confirm + logs_id once Stripe resolves). `src/hook/services.js`'s
// `accounts` object literal defines `AddBalance` twice — PUT /accounts/addbalance
// then POST /accounts/add-balance — so the second definition silently wins
// at runtime and the PUT route is dead. Ported to match the actual (POST)
// runtime behavior, not the shadowed PUT one.
export function useAddBalance() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/accounts/add-balance", { payload }),
  });
}
